// `uploadData`: the outbox → `POST /commands` (plan §9.3, D-024, D-042).
//
//   - Batches go out oldest first, as queued. Only outbox rows are sent; other queued
//     changes are optimistic local effects, replaced by the server's data on the next sync.
//   - A batch leaves the queue only when `canCompleteBatch` (@enp/domain) says every command
//     got a final answer. `held` is final: the server keeps the command for a manager and the
//     outbox moves on (recorded locally so the station can say "sync needs a manager").
//   - Any transient answer (`retry_later`, `not_processed`), a missing answer, no network or
//     a server error keeps the whole batch queued, in order, and the engine retries later.
//   - `upgrade_required` raises the minimum-version gate and blocks uploads until the app is
//     updated; the commands stay queued. So does a 426 when the token is refreshed (the
//     station never gets as far as `/commands`, review CR3-1).
//   - There is no terminal "failed" state: nothing is ever dropped.

import {
  type CommandBatchResponse,
  type CommandResponse,
  MAX_COMMAND_BATCH,
  type UpgradeNotice,
  canCompleteBatch,
  upgradeNoticeFrom,
} from '@enp/domain';
import { ApiError, type ApiClient } from './api.js';
import { type DeviceAuth, UpgradeRequiredError } from './credentials.js';
import { DEVICE_STATE_KEYS, readDeviceState, writeDeviceState } from './device-state.js';
import type { LocalReader, LocalTx, SyncEngine } from './engine.js';
import { envelopeFromOp, isOutboxOp } from './outbox.js';
import { calmRefusal, stationFlagsText } from './outcomes.js';
import { recordBatchAnswer } from './production.js';
import { recordHandoverAnswer } from './handover.js';
import { recordReceiptAnswer } from './receiving.js';

/** The minimum-version gate (plan §9.5): uploads stop until the station app is updated. */
export interface UpgradeGate {
  /** The app version that was refused; an update clears the gate. */
  appVersion: string;
  reason: 'app_version' | 'schema_version';
  minAppVersion?: string;
  raisedAt: string;
}

/** Uploads are blocked until the app is updated. The commands stay queued. */
export class UploadBlockedError extends Error {
  constructor(readonly gate: UpgradeGate) {
    super('This station app must be updated before it can sync');
    this.name = 'UploadBlockedError';
  }
}

/** The server gave a transient answer; the batch stays queued and is sent again later. */
export class TransientUploadError extends Error {
  constructor(
    message: string,
    readonly answer?: CommandResponse,
  ) {
    super(message);
    this.name = 'TransientUploadError';
  }
}

/** The gate, if raised for this app version (an older gate is cleared by the update). */
export async function readUpgradeGate(
  db: LocalReader,
  appVersion: string,
): Promise<UpgradeGate | null> {
  const gate = await readDeviceState<UpgradeGate>(db, DEVICE_STATE_KEYS.upgradeGate);
  return gate && gate.appVersion === appVersion ? gate : null;
}

/** The gate for a server's upgrade notice (any route: 426, config, commands). */
export function gateFromNotice(notice: UpgradeNotice, appVersion: string, now: Date): UpgradeGate {
  return {
    appVersion,
    reason: notice.reason,
    ...(notice.min_app_version ? { minAppVersion: notice.min_app_version } : {}),
    raisedAt: now.toISOString(),
  };
}

/**
 * Raises the minimum-version gate (plan §9.5): uploads stop, the station shows "update
 * needed", Electron's update controller and the tablets' store prompt react to it.
 */
export async function raiseUpgradeGate(
  engine: Pick<SyncEngine, 'writeTransaction'>,
  appVersion: string,
  notice: UpgradeNotice,
  now: Date,
): Promise<UpgradeGate> {
  const gate = gateFromNotice(notice, appVersion, now);
  await engine.writeTransaction((tx) =>
    writeDeviceState(tx, DEVICE_STATE_KEYS.upgradeGate, gate, now),
  );
  return gate;
}

export interface CommandUploaderOptions {
  api: ApiClient;
  auth: DeviceAuth;
  appVersion: string;
  now?: () => Date;
  /** Commands per request (≤ MAX_COMMAND_BATCH). */
  batchSize?: number;
  /** Told after every answered batch (e.g. to refresh counts). */
  onAnswered?: (response: CommandBatchResponse) => void;
}

/** Builds the connector's `uploadData`. */
export function createCommandUploader(options: CommandUploaderOptions) {
  const now = options.now ?? (() => new Date());
  const batchSize = Math.min(options.batchSize ?? MAX_COMMAND_BATCH, MAX_COMMAND_BATCH);

  return async function uploadData(engine: SyncEngine): Promise<void> {
    const gate = await readUpgradeGate(engine, options.appVersion);
    if (gate) throw new UploadBlockedError(gate);

    for (;;) {
      const batch = await engine.nextUploadBatch(batchSize);
      if (!batch) return;
      const commands = batch.ops.filter(isOutboxOp).map(envelopeFromOp);
      if (commands.length === 0) {
        // Only local effects: nothing to send; they are replaced by the next download.
        await batch.complete();
        continue;
      }

      let response: CommandBatchResponse;
      try {
        response = await post(options, commands);
      } catch (err) {
        // A 426 on the token refresh: the same gate as an `upgrade_required` answer.
        if (err instanceof UpgradeRequiredError) {
          throw new UploadBlockedError(
            await raiseUpgradeGate(engine, options.appVersion, err.notice, now()),
          );
        }
        throw err;
      }
      options.onAnswered?.(response);

      if (response.upgrade_required) {
        // The batch's `upgrade` object (D-050); else the first `upgrade_required` answer.
        const answer = response.results.find((r) => r.status === 'upgrade_required');
        const notice: UpgradeNotice = upgradeNoticeFrom(response) ?? {
          reason:
            answer?.status === 'upgrade_required' && answer.reason === 'schema_version'
              ? 'schema_version'
              : 'app_version',
          min_app_version:
            answer?.status === 'upgrade_required' ? (answer.min_app_version ?? null) : null,
        };
        const raised = await raiseUpgradeGate(engine, options.appVersion, notice, now());
        throw new UploadBlockedError(raised);
      }

      if (!canCompleteBatch(commands.length, response)) {
        const transient = response.results.find(
          (r) => r.status === 'retry_later' || r.status === 'not_processed',
        );
        throw new TransientUploadError(
          transient?.status === 'retry_later'
            ? `The server will take these later (${transient.reason})`
            : 'The server did not answer every command; sending again later',
          transient,
        );
      }

      // Every answer is final. Keep the ones a person must see, then let the batch go.
      const byId = new Map(
        commands.map((c) => [String((c as { command_id?: unknown }).command_id), c]),
      );
      await engine.writeTransaction(async (tx) => {
        for (const answer of response.results) {
          // A production batch action keeps its answer (the queue shows it, D-024).
          await recordBatchAnswer(tx, answer, now());
          // So does a delivery received here (the Receiving view shows it).
          await recordReceiptAnswer(tx, answer, now());
          // And an order handed over here (the orders view shows it, WP7.3).
          await recordHandoverAnswer(tx, answer, now());
          if (answer.status === 'applied_with_flags') {
            // Only the notes people here should hear about (never `needs_approval`, D-056).
            const command = byId.get(String(answer.command_id));
            const text = stationFlagsText(answer.flags, command);
            if (text) {
              await recordOutcome(tx, answer, command, now(), {
                status: 'flagged',
                reason: text,
              });
            }
            continue;
          }
          if (answer.status !== 'held' && answer.status !== 'rejected') continue;
          const command = byId.get(String(answer.command_id)) as { type?: unknown } | undefined;
          const reason = 'reason' in answer ? answer.reason : undefined;
          // The batch's card says it calmly; no alarm for "the office got there first".
          if (answer.status === 'rejected' && calmRefusal(command?.type, reason)) continue;
          await recordOutcome(tx, answer, command, now());
        }
      });
      await batch.complete();
    }
  };
}

async function post(options: CommandUploaderOptions, commands: unknown[]) {
  for (let attempt = 0; ; attempt++) {
    const token = await options.auth.getToken();
    try {
      return await options.api.postCommands(token.token, commands);
    } catch (err) {
      // A token can die early (key rotation, clock skew): refresh once, then give up for now.
      if (err instanceof ApiError && err.status === 401 && attempt === 0) {
        options.auth.invalidateToken();
        continue;
      }
      throw err;
    }
  }
}

async function recordOutcome(
  tx: LocalTx,
  answer: CommandResponse,
  command: unknown,
  now: Date,
  as?: { status: 'flagged'; reason: string },
): Promise<void> {
  const id = answer.command_id;
  if (!id) return;
  const c = (command ?? {}) as Record<string, unknown>;
  const reason =
    as?.reason ?? ('reason' in answer && typeof answer.reason === 'string' ? answer.reason : null);
  const holdId = answer.status === 'held' ? answer.hold_id : null;
  const message = 'message' in answer && typeof answer.message === 'string' ? answer.message : null;
  const values = [
    typeof c['type'] === 'string' ? c['type'] : null,
    as?.status ?? answer.status,
    reason,
    holdId,
    message,
    typeof c['device_seq'] === 'number' ? c['device_seq'] : null,
    typeof c['occurred_at'] === 'string' ? c['occurred_at'] : null,
    typeof c['staff_id'] === 'string' ? c['staff_id'] : null,
    now.toISOString(),
  ];
  const exists = await tx.getOptional<{ id: string }>(
    'SELECT id FROM command_outcome WHERE id = ?',
    [id],
  );
  if (exists) {
    await tx.execute(
      `UPDATE command_outcome SET type = ?, status = ?, reason = ?, hold_id = ?, message = ?,
         device_seq = ?, occurred_at = ?, staff_id = ?, recorded_at = ? WHERE id = ?`,
      [...values, id],
    );
  } else {
    await tx.execute(
      `INSERT INTO command_outcome (type, status, reason, hold_id, message, device_seq, occurred_at, staff_id, recorded_at, id)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [...values, id],
    );
  }
}
